import React, { Fragment, useState } from 'react'
import styled from 'styled-components'
import * as R from 'ramda'

import { AdminContainer } from '../'
import BrandSettings from 'organisms/BrandSettings'
import useModal from 'hooks/useModal'
import { Button, Form } from 'semantic-ui-react'
import useAxios, { mutate } from 'hooks/useAxios'
import { toast } from 'libs/toast'
import { UpgradeToProFeatureTooltip } from 'components/ProLabelTooltip'
import usePermission from 'hooks/usePermission'
import PageHeaderContainer from 'shared/components/PageHeader/container'
import { LimitFeatureButton } from 'components/LimitFeatureButton'
import { CrownBadge } from 'components/ProFeatureLabel'
import { AllowPermission } from 'shared/components/AllowPermission'
import constants from 'config/constants'
import { validateRequiredText } from 'libs/form-validation'
import ErrorText from 'shared/components/forms/ErrorText'

const LinkLayout = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
`

export default function AdminBrands() {
  const { data: enableCustomizeBranding } = useAxios({
    url: 'billing/plans/feature-toggle/',
    method: 'post',
    data: { feature: 'custom_survey_branding' },
  })

  const { data: brands, loading: brandsLoading } = useAxios({ url: 'brand/brands/' })
  const totalBrand = R.path(['results'], brands) || []

  return (
    <Fragment>
      <PageHeaderContainer
        title="Brands"
        rightSlot={
          <CreateNewBrandButton totalBrand={totalBrand.length} brandsLoading={brandsLoading} />
        }
      />
      <AdminContainer>
        <div className="ui segment admin-section">
          <LinkLayout>
            <a
              href="https://help.simplesat.io/en/articles/5051375-branding-design-and-customization"
              target="_blank"
              rel="noreferrer"
            >
              <i className="icon far fa-book" />
              Learn more about branding design and customization
            </a>
          </LinkLayout>
          <div className="mt3">
            <BrandSettings
              enableCustomizeBranding={R.prop('enabled', enableCustomizeBranding)}
              brands={brands}
            />
          </div>
        </div>
      </AdminContainer>
    </Fragment>
  )
}

function CreateNewBrandButton({ totalBrand, brandsLoading }) {
  const { user } = usePermission()
  const { Modal, open, close } = useModal()
  const { fetch: createBrand, loading } = useAxios({
    url: 'brand/brands/',
    method: 'post',
    skip: true,
  })
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')

  const brandLimit = R.path(['subscription', 'subscription_items_metadata', 'brands'], user)

  function handleOpen() {
    setName('')
    setNameError('')
    open()
  }

  function handleClose() {
    setName('')
    setNameError('')
    close()
  }

  function createNewBrand() {
    const error = validateRequiredText('Brand name', name)
    if (error) {
      setNameError(error)
      return
    }

    createBrand({
      data: { name },
    })
      .then((response) => {
        handleClose()
        mutate(
          'brand/brands/',
          (brands) => ({ results: [...brands.results, response.data] }),
          false
        )
        toast.success('Nice work! A new brand has been created.')
      })
      .catch(() => {
        toast.error('Oops! Something went wrong. Please contact Simplesat support for help.')
      })
  }

  if (brandLimit && parseInt(brandLimit) === 0) {
    return (
      <AllowPermission permission={constants.permissions.manageBrands}>
        <UpgradeToProFeatureTooltip
          trigger={
            <div>
              <DisabledCreateBrandButton />
            </div>
          }
          title={'Multiple brands'}
          description={
            'Multiple brands allows you to use surveys with different logos and design settings in a single Simplesat account.'
          }
          learnMoreLink="http://help.simplesat.io/en/articles/5051375-branding-design-and-customization#h_cfdc0e236e"
        />
      </AllowPermission>
    )
  }

  return (
    <Fragment>
      <AllowPermission permission={constants.permissions.manageBrands}>
        <LimitFeatureButton
          featureLimit={brandLimit}
          usageCount={totalBrand}
          handleClick={handleOpen}
          descriptionTitle={
            <span className="flex items-center">
              Brand limit reached <CrownBadge />
            </span>
          }
          descriptionContent={
            "You've reached your account's brand limit. Please remove brands or upgrade your plan to add more."
          }
          learnMoreLink={
            'https://help.simplesat.io/en/articles/5051375-branding-design-and-customization'
          }
          buttonText={'New brand'}
          loading={brandsLoading}
        />
      </AllowPermission>

      <Modal size="tiny" onClose={handleClose}>
        <Modal.Header>Create new brand</Modal.Header>
        <Modal.Content>
          <Form noValidate onSubmit={createNewBrand}>
            <Form.Field error={Boolean(nameError)}>
              <label>Brand name</label>
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (nameError) {
                    setNameError('')
                  }
                }}
              />
              {nameError && <ErrorText>{nameError}</ErrorText>}
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button primary onClick={createNewBrand} disabled={loading} loading={loading}>
            Create brand
          </Button>
        </Modal.Actions>
      </Modal>
    </Fragment>
  )
}

function DisabledCreateBrandButton() {
  return (
    <Button primary disabled>
      <i className="icon far fa-plus" /> New brand
    </Button>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Admin/Brands/index.js